import api from '@/lib/api';
import type {
  DepartmentWithRelations,
  DivisionWithCount,
  EmploymentTypeWithCount,
  LocationWithCount,
  PositionWithRelations,
  SeniorityLevelWithCount,
} from '@/types/employee';
import { CACHE_TIME, type PositionCategory } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** Query key factories */
export const departmentsKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentsKeys.all, 'list'] as const,
  list: () => [...departmentsKeys.lists()] as const,
  details: () => [...departmentsKeys.all, 'detail'] as const,
  detail: (id: string) => [...departmentsKeys.details(), id] as const,
};

export const positionsKeys = {
  all: ['positions'] as const,
  lists: () => [...positionsKeys.all, 'list'] as const,
  list: () => [...positionsKeys.lists()] as const,
  details: () => [...positionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...positionsKeys.details(), id] as const,
};

export const locationsKeys = {
  all: ['locations'] as const,
  lists: () => [...locationsKeys.all, 'list'] as const,
  list: () => [...locationsKeys.lists()] as const,
  details: () => [...locationsKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationsKeys.details(), id] as const,
};

export const seniorityLevelsKeys = {
  all: ['seniority-levels'] as const,
  lists: () => [...seniorityLevelsKeys.all, 'list'] as const,
  list: () => [...seniorityLevelsKeys.lists()] as const,
};

export const divisionsKeys = {
  all: ['divisions'] as const,
  lists: () => [...divisionsKeys.all, 'list'] as const,
  list: () => [...divisionsKeys.lists()] as const,
  details: () => [...divisionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...divisionsKeys.details(), id] as const,
};

export const employmentTypesKeys = {
  all: ['employment-types'] as const,
  lists: () => [...employmentTypesKeys.all, 'list'] as const,
  list: () => [...employmentTypesKeys.lists()] as const,
  details: () => [...employmentTypesKeys.all, 'detail'] as const,
  detail: (id: string) => [...employmentTypesKeys.details(), id] as const,
};

// API functions
async function getDepartments(): Promise<DepartmentWithRelations[]> {
  const { data } = await api.get<DepartmentWithRelations[]>('/departments');
  return data;
}

async function getPositions(includeInactive = false): Promise<PositionWithRelations[]> {
  const { data } = await api.get<PositionWithRelations[]>('/positions', {
    params: includeInactive ? { includeInactive: true } : undefined,
  });
  return data;
}

async function getLocations(): Promise<LocationWithCount[]> {
  const { data } = await api.get<LocationWithCount[]>('/locations');
  return data;
}

async function getSeniorityLevels(): Promise<SeniorityLevelWithCount[]> {
  const { data } = await api.get<SeniorityLevelWithCount[]>('/seniority-levels');
  return data;
}

async function getDivisions(): Promise<DivisionWithCount[]> {
  const { data } = await api.get<DivisionWithCount[]>('/divisions');
  return data;
}

async function getEmploymentTypes(includeInactive = false): Promise<EmploymentTypeWithCount[]> {
  const params = includeInactive ? { includeInactive: 'true' } : {};
  const { data } = await api.get<EmploymentTypeWithCount[]>('/employment-types', { params });
  return data;
}

export interface CreateEmploymentTypeInput {
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateEmploymentTypeInput {
  name?: string;
  description?: string;
  isActive?: boolean;
}

async function createEmploymentType(
  input: CreateEmploymentTypeInput
): Promise<EmploymentTypeWithCount> {
  const { data } = await api.post<EmploymentTypeWithCount>('/employment-types', input);
  return data;
}

async function updateEmploymentType(
  id: string,
  input: UpdateEmploymentTypeInput
): Promise<EmploymentTypeWithCount> {
  const { data } = await api.put<EmploymentTypeWithCount>(`/employment-types/${id}`, input);
  return data;
}

async function deleteEmploymentType(id: string): Promise<void> {
  await api.delete(`/employment-types/${id}`);
}

/**
 * Hook to fetch all departments
 */
export function useDepartments() {
  return useQuery({
    queryKey: departmentsKeys.list(),
    queryFn: getDepartments,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch all positions
 */
export function usePositions(includeInactive = false) {
  return useQuery({
    queryKey: [...positionsKeys.list(), { includeInactive }],
    queryFn: () => getPositions(includeInactive),
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch all locations
 */
export function useLocations() {
  return useQuery({
    queryKey: locationsKeys.list(),
    queryFn: getLocations,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch all seniority levels
 */
export function useSeniorityLevels() {
  return useQuery({
    queryKey: seniorityLevelsKeys.list(),
    queryFn: getSeniorityLevels,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch all divisions
 */
export function useDivisions() {
  return useQuery({
    queryKey: divisionsKeys.list(),
    queryFn: getDivisions,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch all employment types
 */
export function useEmploymentTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...employmentTypesKeys.list(), { includeInactive }],
    queryFn: () => getEmploymentTypes(includeInactive),
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to create an employment type
 */
export function useCreateEmploymentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEmploymentType,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypesKeys.all });
    },
  });
}

/**
 * Hook to update an employment type
 */
export function useUpdateEmploymentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateEmploymentTypeInput & { id: string }) =>
      updateEmploymentType(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypesKeys.all });
    },
  });
}

/**
 * Hook to delete (deactivate) an employment type
 */
export function useDeleteEmploymentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEmploymentType,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      // Force refetch all employment types queries
      queryClient.invalidateQueries({ queryKey: employmentTypesKeys.all });
      queryClient.refetchQueries({ queryKey: employmentTypesKeys.all });
    },
  });
}

// ============================================
// POSITIONS CRUD (Settings → Positions)
// ============================================

export type { PositionCategory };

export interface CreatePositionInput {
  title: string;
  description?: string;
  departmentId?: string;
  /** Omitted → the backend auto-detects the category from the title. */
  category?: PositionCategory;
  /** Lucide icon name; null resets to the category-derived auto icon. */
  icon?: string | null;
}

export type UpdatePositionInput = Partial<CreatePositionInput>;

async function createPosition(input: CreatePositionInput): Promise<PositionWithRelations> {
  const { data } = await api.post<PositionWithRelations>('/positions', input);
  return data;
}

async function updatePosition(
  id: string,
  input: UpdatePositionInput
): Promise<PositionWithRelations> {
  const { data } = await api.put<PositionWithRelations>(`/positions/${id}`, input);
  return data;
}

async function deletePosition(id: string): Promise<void> {
  await api.delete(`/positions/${id}`);
}

async function togglePositionActive(id: string): Promise<PositionWithRelations> {
  const { data } = await api.put<PositionWithRelations>(`/positions/${id}/toggle-active`);
  return data;
}

export function useCreatePosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPosition,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: positionsKeys.all });
    },
  });
}

export function useUpdatePosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdatePositionInput & { id: string }) =>
      updatePosition(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: positionsKeys.all });
    },
  });
}

export function useDeletePosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePosition,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: positionsKeys.all });
      queryClient.refetchQueries({ queryKey: positionsKeys.all });
    },
  });
}

export function useTogglePositionActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: togglePositionActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: positionsKeys.all });
      queryClient.refetchQueries({ queryKey: positionsKeys.all });
    },
  });
}
